import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FilteredPurchase, IndividualPayments, PurchaseFormValues, PurchaseSummary } from '../../../types/purchases/BasePurchase';

interface BasePurchaseState {
    form: PurchaseFormValues;
    formErrors: Record<string, string>;
    purchaseSaved: boolean;
    purchaseLoaded: boolean;
    purchaseSettlementLoaded: boolean;
    purchaseToViewLoaded: boolean;
    pendingPurchaseCount: number;
    finalizedPurchaseCount: number;
    pendingPurchaseList: PurchaseSummary[];
    finalizedPurchaseList: PurchaseSummary[],
    filteredPurchaseList: FilteredPurchase[];
    paginator: any,
    pageSize: number,
    isSubmitting: boolean;
    activeDocumentType: PurchaseFormValues['documentType'] | null;
    paymentConditionCode: string | null;
    correlativeRequested: boolean;
    showPdfModal: boolean;
    showDtePdfModal: boolean;
}

// Estado inicial del formulario
const initialFormState: PurchaseFormValues = {
    id: undefined,
    branchId: null,
    documentType: null,
    documentNumber: null,
    correlative: {
        id: null,
        number: null,
    },
    purchaseDate: null,
    supplier: null,
    paymentCondition: null,
    comments: null,
    products: [],
    paymentMethod: undefined,
    individualPayments: undefined,
};

const initialState: BasePurchaseState = {
    form: initialFormState,
    formErrors: {},
    pendingPurchaseCount: 0,
    finalizedPurchaseCount: 0,
    pendingPurchaseList: [],
    finalizedPurchaseList: [],
    filteredPurchaseList: [],
    paginator: null,
    pageSize: 10,
    purchaseSaved: false,
    purchaseLoaded: false,
    purchaseSettlementLoaded: false,
    purchaseToViewLoaded: false,
    isSubmitting: false,
    activeDocumentType: null,
    paymentConditionCode: '',
    correlativeRequested: false,
    showPdfModal: false,
    showDtePdfModal: false
};

const basePurchaseSlice = createSlice({
    name: 'basePurchase',
    initialState,
    reducers: {
        /**
         * ✅ Establece el tipo de documento activo que se usará para determinar qué sección mostrar.
         *
         * Se utiliza para prevenir que el usuario cambie la sección accidentalmente desde el dropdown.
         */
        setActiveDocumentType(state, action: PayloadAction<PurchaseFormValues['documentType']>) {
            state.activeDocumentType = action.payload;
        },


        /**
         * ✅ Marca la compra como guardada o no.
         *
         * @param state - Estado actual del slice.
         * @param action - Booleano que indica si la compra fue guardada correctamente.
         *
         * - true: La compra fue generada exitosamente.
         * - false: La compra aún no se ha generado o hubo un error.
         */
        setPurchaseSaved: (state, action: PayloadAction<boolean>) => {
            state.purchaseSaved = action.payload;
        },

        /**
         * ✅ Indica si los datos de la compra han sido cargados correctamente.
         *
         * @param state - Estado actual del slice.
         * @param action - Booleano que representa si los datos ya están disponibles.
         *
         * Se utiliza, por ejemplo, para habilitar la vista de edición tras obtener los datos.
         */
        setPurchaseLoaded: (state, action: PayloadAction<boolean>) => {
            state.purchaseLoaded = action.payload;
        },


        /**
         * ✅ Indica si la compra a visualizar (modo solo lectura) ha sido cargada completamente.
         *
         * @param state - Estado actual del slice.
         * @param action - Booleano que indica si los datos de la compra ya están disponibles.
         *
         * Se activa cuando se abre el modal de "Ver Compra".
         */
        setPurchaseToViewLoaded: (state, action: PayloadAction<boolean>) => {
            state.purchaseToViewLoaded = action.payload;
        },


        /**
         * ⚖️ Indica si la compra en modo de asentamiento ha sido cargada completamente.
         *
         * @param state - Estado actual del slice.
         * @param action - Booleano que indica si los datos de la compra a asentar ya están disponibles.
         *
         * Se activa cuando se recupera una compra con la intención de asentar productos.
         */
        setPurchaseSettlementLoaded: (state, action: PayloadAction<boolean>) => {
            state.purchaseSettlementLoaded = action.payload;
        },


        /**
         * 🔄 Indica si el formulario de compra está en proceso de envío.
         *
         * @param state - Estado actual del slice.
         * @param action - Booleano que representa si se está enviando o no.
         *
         * Sirve para bloquear botones o mostrar loaders mientras se realiza una petición.
         */
        setIsSubmitting(state, action: PayloadAction<boolean>) {
            state.isSubmitting = action.payload;
        },

        /**
         * 🧹 Limpia la lista de compras filtradas y su paginador asociado.
         *
         * @param state - Estado actual del slice.
         *
         * Se usa al cambiar de vista o cuando se desea reiniciar la tabla de compras.
         */
        clearFilteredPurchaseList(state) {
            state.filteredPurchaseList = [];
            state.paginator = null;
        },


        /**
         * 🧠 Asigna el formulario de compra con los datos proporcionados.
         *
         * @param state - Estado actual del slice.
         * @param action - Objeto `PurchaseFormValues` que contiene el encabezado y productos de la compra.
         *
         * ✅ Este reducer se usa cuando:
         *  - Se genera una nueva compra y se recibe el objeto completo desde el backend.
         *  - Se edita o se visualiza una compra existente y se cargan sus datos en el formulario.
         *
         * ⚠️ Importante:
         *  - Mezcla el `initialFormState` con los datos recibidos para asegurar que no queden campos indefinidos.
         *  - Asegura que la lista `products` no sea `undefined`, sino un arreglo vacío por defecto.
         *  - Limpia errores del formulario (`formErrors`) para evitar mostrar errores antiguos.
         */
        setPurchaseForm(state, action: PayloadAction<PurchaseFormValues>) {
            state.form = {
                ...initialFormState,
                ...action.payload,
                products: action.payload.products ?? [] // 🛡️ Evita que sea undefined
            };
            state.formErrors = {};
        },




        /**
         * 📝 Actualiza un campo específico del formulario de compra.
         *
         * @param state - Estado actual del slice.
         * @param action - Objeto con `field` (clave del formulario) y su `value` correspondiente.
         *
         * ✅ Útil para modificar valores individuales del formulario sin tener que sobreescribir todo.
         * 🔁 Mantiene todos los campos anteriores y solo sobrescribe el campo que se desea actualizar.
         *
         * Ejemplo:
         *   updatePurchaseField({ field: 'documentNumber', value: 'INV-001' })
         */
        updatePurchaseField(state, action: PayloadAction<{ field: keyof PurchaseFormValues; value: any }>) {
            const { field, value } = action.payload;
            state.form = {
                ...state.form,        // ✅ Mantiene lo anterior
                [field]: value,       // 🔄 Sobrescribe el campo que se actualiza
            };
        },


        /**
        * 💳 Actualiza uno o varios campos del objeto `individualPayments` del formulario de venta.
        *
        * @param state - Estado actual del slice.
        * @param action - Objeto parcial de `IndividualPayments` con los campos que se desean actualizar.
        *
        * ✅ Ideal para:
        *  - Actualizar datos de forma de pago individual (efectivo, tarjeta, transferencia, crédito).
         *  - Evitar sobrescribir accidentalmente el objeto completo de `individualPayments`.
         *  - Mantener la lógica de actualización clara y segura.
         *
         * Ejemplo de uso:
         * ```ts
         * dispatch(updateIndividualPayments({
         *   cash: {
         *     amountReceived: 10,
         *     change: 5
         *   }
         * }));
         * ```
         */
        updateIndividualPayments(state, action: PayloadAction<Partial<IndividualPayments>>) {
            state.form.individualPayments = {
                ...state.form.individualPayments,
                ...action.payload
            };
        },

        updatePurchaseAfterForceAnnul(
            state,
            action: PayloadAction<{
                id: number;
                status: FilteredPurchase['status'];
                documentNumber: string;
            }>
        ) {
            const { id, status, documentNumber } = action.payload;

            const index = state.filteredPurchaseList.findIndex(
                (p) => p.id === id
            );

            if (index !== -1) {
                state.filteredPurchaseList[index].status = status;
                state.filteredPurchaseList[index].documentNumber = documentNumber;
            }
        },

        /**
         * 🧹 Limpia por completo el objeto `individualPayments` del formulario de venta.
         *
         * @param state - Estado actual del slice.
         *
         * ✅ Se usa típicamente cuando:
         *  - Se cambia el `paymentMethod` y se desea limpiar los datos previos.
         *  - Se reinicia el formulario de venta.
         *  - Se evita enviar datos no deseados al backend.
         */
        clearIndividualPayments(state) {
            state.form.individualPayments = {
                cash: undefined,               // 01
                debitCard: undefined,          // 02
                creditCard: undefined,         // 03
                check: undefined,              // 04
                bankTransfer: undefined,       // 05
                electronicMoney: undefined,    // 08
                eWallet: undefined,            // 09
                bitcoin: undefined,            // 11
                crypto: undefined,             // 12
                accountsReceivable: undefined, // 13
                bankDraft: undefined,          // 14
                other: undefined,              // 99
            };
        },

        /**
         * 🔄 Actualiza el código de la condición de pago seleccionada
         *
         * @param state - Estado actual del slice.
         * @param action - String con el código (por ejemplo: 'CONTADO', 'CREDITO', 'OTRO').
         *
         * ✅ Este reducer te permite saber en cualquier parte del estado cuál fue
         * la condición seleccionada para mostrar/ocultar secciones dinámicamente.
         */
        setPaymentConditionCode(state, action: PayloadAction<string | null>) {
            const newCode = action.payload;

            // 🔄 Siempre limpiamos los pagos al cambiar la condición de pago
            state.form.individualPayments = {
                cash: undefined,
                debitCard: undefined,
                creditCard: undefined,
                check: undefined,
                bankTransfer: undefined,
                electronicMoney: undefined,
                eWallet: undefined,
                bitcoin: undefined,
                crypto: undefined,
                accountsReceivable: undefined,
                bankDraft: undefined,
                other: undefined,
            };

            state.paymentConditionCode = newCode;
        },


        /**
         * 🔄 Actualiza un producto existente dentro de la lista de productos del formulario.
         *
         * @param state - Estado actual del slice.
         * @param action - Objeto `product` actualizado que debe contener un `id` para encontrarlo.
         *
         * ✅ Busca el producto en la lista por su `id`.
         * 🔁 Si lo encuentra, lo reemplaza con los nuevos datos recibidos.
         * ⚠️ No hace nada si el producto no se encuentra.
         *
         * Este reducer se usa principalmente luego de editar un producto en el backend.
         */
        updatePurchaseProduct(state, action: PayloadAction<any>) {
            const updated = action.payload;
            const index = state.form.products.findIndex(p => p.id === updated.id);
            if (index !== -1) {
                state.form.products[index] = updated;
            }
        },

        /**
         * 📦 Actualiza el estado (`status`) de una compra dentro de la lista filtrada.
         *
         * @param state - Estado actual del slice.
         * @param action - Objeto que contiene el `id` de la compra y el nuevo `status` ('PENDING', 'FINALIZED', 'CANCELLED').
         *
         * ✅ Útil para actualizar el estado visual en la tabla del historial sin recargar toda la lista.
         * 🔄 Busca la compra por su ID y actualiza su estado directamente en `filteredPurchaseList`.
         */
        updatePurchaseStatus: (
            state,
            action: PayloadAction<{ id: number; status: FilteredPurchase['status']; hasDteError?: boolean }>
        ) => {
            const { id, status, hasDteError } = action.payload;
            const index = state.filteredPurchaseList.findIndex((p) => p.id === id);
            if (index !== -1) {
                state.filteredPurchaseList[index].status = status;
                if (hasDteError !== undefined) state.filteredPurchaseList[index].hasDteError = hasDteError;
            }
        },


        /**
         * ➕ Agrega un nuevo producto a la lista de productos del formulario de compra.
         *
         * @param state - Estado actual del slice.
         * @param action - Producto que se desea agregar (objeto con datos como id, nombre, cantidad, etc.).
         *
         * ✅ Añade el producto al arreglo `products` en `form`.
         * ⚠️ No realiza validaciones aquí; se asume que el producto ya fue validado.
         *
         * Este reducer se utiliza después de guardar un producto en el backend y recibir la respuesta con su estructura final.
         */
        addPurchaseProduct(state, action: PayloadAction<any>) {
            const newProduct = action.payload;

            const index = state.form.products.findIndex(
                (p) => p.id === newProduct.id
            );

            if (index !== -1) {
                // 🔄 Ya existe: lo reemplazamos
                state.form.products[index] = newProduct;
            } else {
                // ➕ No existe: lo agregamos
                state.form.products.push(newProduct);
            }
        },


        /**
         * 🔄 Actualiza los totales de la compra de crédito fiscal.
         *
         * ✅ Se usa después de agregar/editar/eliminar un producto,
         * cuando el backend retorna los nuevos valores de:
         *  - subtotal gravado
         *  - IVA calculado
         *  - IVA percibido (retenido)
         */
        updatePurchaseTotals(
            state,
            action: PayloadAction<{
                taxableSubtotal: number;
                calculatedIVA: number;
                withheldIVA: number;
                perceivedIVA?: number;
            }>
        ) {
            const { taxableSubtotal, calculatedIVA, withheldIVA, perceivedIVA } = action.payload;
            state.form.taxableSubtotal = taxableSubtotal;
            state.form.calculatedIVA = calculatedIVA;
            state.form.withheldIVA = withheldIVA;
            state.form.perceivedIVA = perceivedIVA ?? 0;
        },

        /**
         * 🔄 Actualiza los totales específicos de la compra de crédito fiscal.
         *
         * ✅ Se usa después de agregar/editar/eliminar un producto,
         * cuando el backend retorna los nuevos valores de:
         *  - subtotal gravado
         *  - IVA calculado
         *  - IVA percibido (retenido)
         *  - total (nuevo campo)
         */
        updateTaxCreditTotals(
            state,
            action: PayloadAction<{
                taxableSubtotal: number;
                calculatedIVA: number;
                withheldIVA: number;
                perceivedIVA?: number;
                total: number;
            }>
        ) {
            const { taxableSubtotal, calculatedIVA, withheldIVA, perceivedIVA, total } = action.payload;
            state.form.taxableSubtotal = taxableSubtotal;
            state.form.calculatedIVA = calculatedIVA;
            state.form.withheldIVA = withheldIVA;
            state.form.perceivedIVA = perceivedIVA ?? 0;
            state.form.total = total; // ✅ ahora sí, incluido total
        },


        /**
         * 🔄 Actualiza los totales de la nota de crédito.
         *
         * ✅ Se usa después de agregar/editar/eliminar un producto en la nota de crédito,
         * cuando el backend retorna los nuevos valores de:
         *  - Neto gravado (taxableSubtotal)
         *  - IVA percibido (perceivedIVA)
         *  - Total a devolver (totalToRefund)
         */
        updateCreditNoteTotals(
            state,
            action: PayloadAction<{
                subtotal: number;
                iva: number;
                total: number;
            }>
        ) {
            const { subtotal, iva, total } = action.payload;

            state.form.taxableSubtotal = subtotal;
            state.form.perceivedIVA = iva;
            state.form.totalToRefund = total;
        },

        /**
         * 🔄 Actualiza los totales de la compra para sujeto excluido.
         *
         * ✅ Se utiliza después de agregar, editar o eliminar un producto o servicio
         * en una compra de tipo sujeto excluido. El backend retorna los nuevos valores:
         * 
         * - subtotal: Total sin retención (sumatoria de todos los ítems)
         * - incomeTaxWithholding: Retención de renta aplicada (si corresponde)
         * - total: Monto total a pagar (subtotal - retención)
         */
        updateExcludedSubjectTotals(
            state,
            action: PayloadAction<{
                subtotal: number;
                incomeTaxWithholding: number;
                totalToPay: number;
            }>
        ) {
            const { subtotal, incomeTaxWithholding, totalToPay } = action.payload;
            state.form.subtotal = subtotal;
            state.form.incomeTaxWithholding = incomeTaxWithholding;
            state.form.total = totalToPay;
        },


        /**
         * 🔄 Actualiza los totales de la nota de débito.
         *
         * ✅ Se utiliza después de agregar, editar o eliminar un producto, servicio
         * o ajuste de precio en una nota de débito. El backend retorna los nuevos valores:
         *
         * - taxableSubtotal: Subtotal gravado (sumatoria de líneas sin IVA)
         * - ivaAmount: IVA total calculado sobre el subtotal
         * - totalToCharge: Monto total a cobrar (subtotal + IVA)
         */
        updateDebitNoteTotals(
            state,
            action: PayloadAction<{
                taxableSubtotal: number;
                ivaAmount: number;
                totalToCharge: number;
            }>
        ) {
            const { taxableSubtotal, ivaAmount, totalToCharge } = action.payload;
            state.form.taxableSubtotal = taxableSubtotal;
            state.form.calculatedIVA = ivaAmount;
            state.form.total = totalToCharge;
        },





        /**
         * 🗑️ Elimina un producto existente de la lista del formulario, usando su `id`.
         *
         * @param state - Estado actual del slice.
         * @param action - ID del producto que se desea eliminar.
         *
         * ✅ Filtra la lista de productos y elimina el que coincida con el `id` proporcionado.
         * 🔁 La operación es inmutable: se crea un nuevo arreglo sin el producto eliminado.
         *
         * Este reducer se llama normalmente luego de confirmar y eliminar un producto en el backend.
         */
        deletePurchaseProduct(state, action: PayloadAction<number>) {
            state.form.products = state.form.products.filter(
                (product) => product.id !== action.payload
            );
        },

        /**
         * 🧹 Limpia completamente el formulario de compra.
         *
         * @param state - Estado actual del slice.
         *
         * ✅ Reinicia todos los campos del formulario a su estado inicial.
         * ✅ También limpia los errores del formulario (`formErrors`).
         *
         * Este reducer se utiliza cuando se desea reiniciar el formulario,
         * por ejemplo, después de registrar una compra o al cerrar un modal.
        */
        clearPurchaseForm(state) {
            state.form = initialFormState;
            state.formErrors = {};
        },

        /**
         * 🚨 Establece los errores del formulario de compra.
         *
         * @param state - Estado actual del slice.
         * @param action - Objeto con los errores del formulario en formato { campo: mensaje }.
         *
         * ✅ Asigna directamente los errores proporcionados al estado.
         * 🧠 Se utiliza para mostrar mensajes de validación al usuario en los campos correspondientes.
         */
        setFormErrors(state, action: PayloadAction<Record<string, string>>) {
            state.formErrors = action.payload;
        },

        /**
         * ✅ Limpia todos los errores del formulario.
         *
         * @param state - Estado actual del slice.
         *
         * 🔄 Reinicia `formErrors` a un objeto vacío, eliminando cualquier mensaje de validación previo.
         * 🧹 Útil al cerrar modales, reiniciar formularios o después de una validación exitosa.
         */
        clearFormErrors(state) {
            state.formErrors = {};
        },

        /**
         * 🔢 Actualiza el tamaño de página usado en la paginación del historial de compras.
         *
         * @param state - Estado actual del slice.
         * @param action - Nuevo valor para el tamaño de página (por ejemplo: 10, 25, 50).
         *
         * ✅ Este valor se utiliza al hacer peticiones paginadas al backend.
         * 🔄 El valor se guarda en `state.pageSize`.
         */
        setPageSize(state, action) {
            state.pageSize = action.payload;
        },


        /**
         * 🔢 Establece el número total de compras pendientes.
         *
         * @param state - Estado actual del slice.
         * @param action - Número de compras pendientes que retorna el backend.
         *
         * ✅ Se utiliza, por ejemplo, para mostrar un contador visual de pendientes en la UI.
         */
        setPendingPurchaseCount: (state, action: PayloadAction<number>) => {
            state.pendingPurchaseCount = action.payload;
        },

        /**
         * 🔽 Disminuye en uno el contador de compras pendientes.
         *
         * ✅ Útil cuando se anula una compra pendiente, ya que deja de estar en ese estado.
         * 🧠 Se asegura de no disminuir el valor por debajo de 0.
         *
         * @param state - Estado actual del slice.
         */
        decreasePendingPurchaseCount(state) {
            if (state.pendingPurchaseCount > 0) {
                state.pendingPurchaseCount -= 1;
            }
        },

        /**
         * 📦 Establece la lista de compras pendientes (estado `PENDING`).
         *
         * @param state - Estado actual del slice.
         * @param action - Arreglo de objetos `PurchaseSummary` que representan las compras pendientes.
         *
         * ✅ Se usa al obtener las compras pendientes de la Casa Matriz desde el backend.
         */
        setPendingPurchaseList: (state, action: PayloadAction<PurchaseSummary[]>) => {
            state.pendingPurchaseList = action.payload;
        },

        /**
         * ❌ Elimina una compra pendiente de la lista, usando su ID.
         *
         * ✅ Se utiliza después de anular una compra, para eliminarla del listado visual.
         *
         * @param state - Estado actual del slice.
         * @param action - ID de la compra que se desea remover de `pendingPurchaseList`.
         */
        removePendingPurchaseById(state, action: PayloadAction<number>) {
            state.pendingPurchaseList = state.pendingPurchaseList.filter(
                (purchase) => purchase.id !== action.payload
            );
        },

        /**
         * 🔢 Establece el número total de compras finalizadas.
         */
        setFinalizedPurchaseCount: (state, action: PayloadAction<number>) => {
            state.finalizedPurchaseCount = action.payload;
        },

        /**
         * 🔽 Disminuye en uno el contador de compras finalizadas.
         *
         * ✅ Útil cuando se anula una compra que ya estaba finalizada.
         * 🧠 Se asegura de no disminuir el valor por debajo de 0.
         *
         * @param state - Estado actual del slice.
         */
        decreaseFinalizedPurchaseCount(state) {
            if (state.finalizedPurchaseCount > 0) {
                state.finalizedPurchaseCount -= 1;
            }
        },

        /**
         * 📦 Establece la lista de compras finalizadas (estado `FINALIZED`).
         *
         * @param state - Estado actual del slice.
         * @param action - Arreglo de objetos `PurchaseSummary` que representan las compras finalizadas.
         *
         * ✅ Se usa al obtener las compras finalizadas de la Casa Matriz desde el backend.
         */
        setFinalizedPurchaseList: (state, action: PayloadAction<PurchaseSummary[]>) => {
            state.finalizedPurchaseList = action.payload;
        },

        /**
         * ❌ Elimina una compra finalizada de la lista, usando su ID.
         *
         * ✅ Se utiliza si se anula o se desea remover visualmente una compra finalizada de la lista.
         *
         * @param state - Estado actual del slice.
         * @param action - ID de la compra que se desea remover de `finalizedPurchaseList`.
         */
        removeFinalizedPurchaseById(state, action: PayloadAction<number>) {
            state.finalizedPurchaseList = state.finalizedPurchaseList.filter(
                (purchase) => purchase.id !== action.payload
            );
        },


        /**
         * 🔍 Establece la lista de compras filtradas junto con la información de paginación.
         *
         * @param state - Estado actual del slice.
         * @param action - Objeto con la lista `content` (compras filtradas) y los datos del paginador.
         *
         * ✅ Se usa cuando se aplican filtros en el historial de compras.
         * 🧠 `filteredPurchaseList` se llena con los resultados, y `paginator` guarda info como total de páginas, etc.
         */
        setFilteredPurchaseList: (state, action: PayloadAction<{ content: FilteredPurchase[] }>) => {
            state.filteredPurchaseList = action.payload.content;
            state.paginator = action.payload;
        },

        /**
         * 🔄 Restablece completamente el formulario de compra a su estado inicial.
         *
         * 🧹 Limpia todos los campos del formulario, incluyendo los valores de encabezado y productos.
         * ✅ También reinicia los flags de control como `formErrors`, `purchaseSaved`, `purchaseLoaded`,
         *    `purchaseToViewLoaded` e `isSubmitting` para que la interfaz se comporte como si fuera un registro nuevo.
         *
         * 🧠 Este método se usa comúnmente después de finalizar o cancelar una compra,
         *     o cuando el usuario desea comenzar un nuevo registro desde cero.
         *
         * @param state - Estado actual del slice.
         */
        resetBasePurchaseForm(state) {
            state.form = {
                id: undefined,
                branchId: null,
                documentType: null,
                correlative: null,
                supplier: null,
                documentNumber: '',
                purchaseDate: '',
                paymentCondition: null,
                comments: '',
                products: [],
                totalQuantity: undefined,
                netAmount: undefined,
                freight: undefined,
                insurance: undefined,
                fauca: undefined,
                otherCosts: undefined,
                totalCost: undefined,
                individualPayments: undefined,
                paymentMethod: undefined
            };

            // También debes limpiar estos flags para que la UI se comporte como "registro nuevo"
            state.formErrors = {};
            state.purchaseSaved = false;
            state.purchaseLoaded = false;
            state.purchaseToViewLoaded = false;
            state.activeDocumentType = null;
            state.purchaseSettlementLoaded = false;
            state.isSubmitting = false;
            state.correlativeRequested = false;
        },

        /**
         * 🔄 Actualiza la cantidad asentada (`settledQuantity`) de un producto
         * dentro del formulario de compra usando el ID real del producto (`productId`).
         *
         * ✅ Útil cuando se registra o elimina un asentamiento en tiempo real.
         * ✅ Busca el producto dentro de `form.products` y actualiza solo `settledQuantity`.
         *
         * @param productId - ID del producto real.
         * @param settledQuantity - Nueva cantidad asentada a mostrar.
         */
        updateSettledQuantityByProductId: (
            state,
            action: PayloadAction<{ productId: number; settledQuantity: number }>
        ) => {
            const { productId, settledQuantity } = action.payload;

            const product = state.form.products.find(
                (p: any) => 'productId' in p && p.productId === productId
            );

            if (product && 'settledQuantity' in product) {
                (product as any).settledQuantity = settledQuantity;
            }
        },

        /**
         * 🔁 Actualiza múltiples productos con su cantidad asentada (`settledQuantity`)
         * a partir de una lista de estados de asentamiento general.
         *
         * ✅ Útil después de un asentamiento general en una sucursal.
         */
        updateSettledQuantitiesBatch: (
            state,
            action: PayloadAction<{ productId: number; settledQuantity: number }[]>
        ) => {
            const updates = action.payload;

            updates.forEach(({ productId, settledQuantity }) => {
                const product = state.form.products.find(
                    (p: any) => 'productId' in p && p.productId === productId
                );

                if (product && 'settledQuantity' in product) {
                    (product as any).settledQuantity = settledQuantity;
                }
            });
        },

        /**
         * 🔒 Establece si ya se solicitó un correlativo para la compra.
         *
         * ✅ Permite bloquear el campo "Número de Documento" en el formulario para evitar modificaciones.
         * ✅ Se debe establecer en `true` justo después de solicitar un correlativo automáticamente.
         * ✅ Se puede resetear a `false` si el usuario cambia de tipo de documento o reinicia el formulario.
         *
         * @param {boolean} action.payload - `true` si el correlativo fue solicitado; `false` para reiniciar.
         *
         * @example
         * dispatch(setCorrelativeRequested(true));
         */
        setCorrelativeRequested: (state, action: PayloadAction<boolean>) => {
            state.correlativeRequested = action.payload;
        },

        /**
        * 📄 Abre el modal de visualización de venta en formato PDF o ticket.
        *
        * ✅ Este reducer se utiliza cuando:
        *  - El usuario desea **visualizar una venta** en formato PDF (tamaño carta) o en formato ticket.
        *  - Se abre un modal que permite al usuario seleccionar el tipo de visualización deseado.
        *  - Posteriormente, se renderiza el documento correspondiente dentro del modal.
        *
        * 📌 Nota: Este modal no edita datos, solo muestra una vista previa en PDF o ticket.
        */
        setOpenPdfModal(state) {
            state.showPdfModal = true;
        },

        /**
         * ❌ Cierra el modal de visualización de la venta en formato PDF o ticket.
         *
         * ✅ Este reducer se utiliza cuando:
         *  - El usuario cierra manualmente el modal de vista previa.
         *  - Se desea restablecer el estado de visualización después de mostrar el documento.
         */
        setClosePdfModal(state) {
            state.showPdfModal = false;
        },


        setMeta(state, action: PayloadAction<{ id: number }>) {
            state.form.id = action.payload.id;
        },

        clearMeta(state) {
            state.form.id = null;
        },

        /**
         * 📄 Abre el modal exclusivo de vista previa de DTE en PDF.
         *
         * ✅ Se utiliza cuando:
         *  - El usuario desea ver un DTE previamente emitido en formato PDF.
         *  - Este modal no permite cambiar el tipo ni ver ticket, solo visualiza el DTE en pantalla.
         */
        setOpenDtePdfModal(state) {
            state.showDtePdfModal = true;
        },

        /**
         * 🔄 Limpia el número de documento y habilita nuevamente la edición manual.
         *
         * ✅ Se usa cuando se cambia el tipo de documento y el anterior requería correlativo.
         */
        clearDocumentNumberAndCorrelative(state) {
            state.form.documentNumber = null;
            state.form.correlative = { id: null, number: null };
            state.correlativeRequested = false;
            delete state.formErrors.documentNumber;
        },


        /**
         * ❌ Cierra el modal exclusivo de DTE PDF.
         *
         * ✅ Se invoca al cerrar el modal manualmente o al finalizar la visualización.
         */
        setCloseDtePdfModal(state) {
            state.showDtePdfModal = false;
        },

    },
});

export const {
    addPurchaseProduct,
    clearFilteredPurchaseList,
    clearFormErrors,
    clearPurchaseForm,
    decreaseFinalizedPurchaseCount,
    decreasePendingPurchaseCount,
    deletePurchaseProduct,
    removeFinalizedPurchaseById,
    removePendingPurchaseById,
    resetBasePurchaseForm,
    setActiveDocumentType,
    setFilteredPurchaseList,
    setFinalizedPurchaseCount,
    setFinalizedPurchaseList,
    setFormErrors,
    setIsSubmitting,
    setPageSize,
    setPendingPurchaseCount,
    setPendingPurchaseList,
    setPurchaseForm,
    setPurchaseLoaded,
    setPurchaseSaved,
    setPurchaseToViewLoaded,
    setPurchaseSettlementLoaded,
    updateCreditNoteTotals,
    updateDebitNoteTotals,
    updateExcludedSubjectTotals,
    updatePurchaseField,
    updateIndividualPayments,
    updatePurchaseAfterForceAnnul,
    clearIndividualPayments,
    setPaymentConditionCode,
    updatePurchaseProduct,
    updatePurchaseStatus,
    updatePurchaseTotals,
    updateTaxCreditTotals,
    updateSettledQuantityByProductId,
    updateSettledQuantitiesBatch,
    setCorrelativeRequested,
    setClosePdfModal,
    setOpenPdfModal,
    setMeta,
    clearMeta,
    setOpenDtePdfModal,
    clearDocumentNumberAndCorrelative,
    setCloseDtePdfModal,
} = basePurchaseSlice.actions;


export default basePurchaseSlice.reducer;
